import { Trans, useLingui } from '@lingui/react/macro';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import mediaManagerRepository from 'modules/manager/domain/repositories/media-manager-repository';
import { TEMPLATE_NAME_CONFLICT_MESSAGE, isTemplateGroupNameConflict } from 'modules/manager/domain/template-name-conflict';
import MediaManagerTemplateGroupForm, {
  TEMPLATE_GROUP_FORM_INITIAL_VALUES,
  TemplateGroupFormValues,
} from 'modules/manager/ui/components/forms/media-manager-template-group-form';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';

interface Args {
  existingNames: string[];
  onDuplicated: (copy: MediaManagerTemplateGroup) => void;
}

function useDuplicateTemplateGroupModal({ existingNames, onDuplicated }: Args) {
  const { t } = useLingui();
  const tournamentId = useSelector(authStoreUserTournamentId);
  const [group, setGroup] = useState<MediaManagerTemplateGroup | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback((next: MediaManagerTemplateGroup) => {
    setError(null);
    setGroup(next);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  const onSubmit = useCallback(
    async (values: TemplateGroupFormValues) => {
      if (!group || !tournamentId) return;
      setIsLoading(true);
      setError(null);
      try {
        const name = values.name.trim() || group.name;
        const copy = await mediaManagerRepository.copyTemplateGroup({
          templateGroupId: group.id,
          tournamentId,
          name,
        });
        close();
        onDuplicated(copy);
      } catch (err) {
        console.error('Failed to duplicate media manager template group:', err);
        setError(
          isTemplateGroupNameConflict(err) ? t(TEMPLATE_NAME_CONFLICT_MESSAGE) : t`Не удалось дублировать группу, попробуйте ещё раз`,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [group, tournamentId, onDuplicated, close, t],
  );

  return {
    open,
    close,
    canDuplicate: !!tournamentId,
    isDuplicating: isLoading,
    modal: (
      <Modal show={isOpen} onClose={isLoading ? () => undefined : close} size="md">
        <UDText type="title-3" className="mb-5">
          <Trans>Дублировать группу</Trans>
        </UDText>
        <MediaManagerTemplateGroupForm
          key={group?.id ?? 'none'}
          initialValues={{ ...TEMPLATE_GROUP_FORM_INITIAL_VALUES, name: group?.name ?? '' }}
          existingNames={existingNames}
          isLoading={isLoading}
          errorMessage={error}
          submitLabel={<Trans>Дублировать</Trans>}
          onSubmit={onSubmit}
        />
      </Modal>
    ),
  };
}

export default useDuplicateTemplateGroupModal;
